import { boundCheckboxField, BoundForm, boundTextAreaField, boundTextField, FormSectionLayout } from "@homebound/beam";
import { ChangeEventReasonsBoundSelectField } from "src/components/autoPopulateSelects/ChangeEventReasonsBoundSelectField";
import { ChangeEventType, LotType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  BaseChangeEventFormInput,
  BaseChangeEventFormState,
} from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { ReviewApprovers } from "src/routes/projects/change-events/guided/components/ReviewApprovers";
import { ReviewSummaryStats } from "src/routes/projects/change-events/guided/components/ReviewSummaryStats";
import { GuidedChangeEventParams } from "src/routes/routesDef";
import { fail, queryResult } from "src/utils";
import { useReviewStepChangeEventQuery } from "./ReviewStep.generated";

type ReviewStepProps<F> = { formState: BaseChangeEventFormState<F> };

export function ReviewStep<F>(props: ReviewStepProps<F>) {
  const { formState } = props;
  const { idOrAdd } = useRouteParams<GuidedChangeEventParams>();

  const query = useReviewStepChangeEventQuery({ variables: { changeEventId: idOrAdd ?? fail("No change event id") } });

  return queryResult(query, ({ changeEvent }) => {
    const showPreSignedContract =
      changeEvent.type.code === ChangeEventType.External && changeEvent.project.lotType.code === LotType.Boyl;

    return (
      <FormSectionLayout
        title="Ready to Submit"
        description="Review the summary of changes below. The change event will be created on confirmation."
        initialFields={
          <BoundForm<BaseChangeEventFormInput>
            formState={formState}
            rows={[
              { title: boundTextField({ label: "Title" }) },
              {
                reactNodeReasonId: (
                  <ChangeEventReasonsBoundSelectField
                    field={formState.reasonId}
                    label="Reason"
                    workflow={changeEvent.workflow?.code}
                  />
                ),
              },
              { internalNote: boundTextAreaField({ label: "Description" }) },
              ...(showPreSignedContract
                ? [
                    {
                      hasPreSignedContract: boundCheckboxField({
                        label: "Existing signed contract",
                        description:
                          "For BOYL projects, where a signed contract already exists for this home. Skip generating a new PandaDoc and use the existing contract.",
                      }),
                    },
                  ]
                : []),
              { reactNodeSummaryStats: <ReviewSummaryStats changeEvent={changeEvent} /> },
            ]}
          />
        }
        sections={[
          {
            title: "Approvals",
            description: "This change event will be sent to the following people after creation.",
            fields: (
              <ReviewApprovers
                approvers={changeEvent.predictedApprovers}
                clientNoun={changeEvent.project.lotType.clientNoun}
              />
            ),
          },
          {
            title: "What Happens Next?",
            description:
              "This change event will go through the approval process. Once approved, the new line items will appear in the project scope, budget, and finish schedule.",
          },
        ]}
      />
    );
  });
}
